import { Menu, MenuItem, styled } from '@mui/material';
import React from 'react';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import SignUpIcon from '@mui/icons-material/PersonAdd';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import { useAuth } from 'lib/useAuth';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useParams } from 'react-router';
import { ROUTES } from 'pages/routes';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { logout, resetReturnUrl, setReturnUrl } from 'store/auth/authSlice';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { resetRewards } from 'store/basket/basketSlice';
import { ApplicationState } from 'store/store';
import { EPaymentProvider } from 'components/settings/model/Settings';

interface AppBarUserMenuProps {
    anchorEl?: HTMLElement;
    menuId: string;
    onClose: () => void;
    onAuthActionClick?: () => Promise<void>;
}

const StyledIcon = styled('div')(({ theme }) => ({
    marginRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
}));

const paymentProvidersWithCardSupport: string[] = [
    EPaymentProvider.JUDOPAY,
    EPaymentProvider.SQUARE,
    EPaymentProvider.STRIPE,
    EPaymentProvider.WORLDPAY
];

export const AppBarUserMenu: React.FC<AppBarUserMenuProps> = ({
    anchorEl,
    menuId,
    onClose,
    onAuthActionClick
}) => {
    const { merchantId } = useParams();
    const { pathname, search } = useLocation();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { isLoggedIn, user } = useAuth();
    const { push, replace } = useLocalHistory();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const fullPath = React.useMemo(() => pathname + search, [pathname, search]);
    const userId = React.useMemo(() => (user ? user._id : ''), [user]);
    const isOnSummaryPage = React.useMemo(
        () =>
            !!matchPath({ path: ROUTES.ORDER.SUMMARY }, pathname) ||
            !!matchPath({ path: ROUTES.LEGACY.SUMMARY }, pathname),
        [pathname]
    );

    const handleProfileClick = React.useCallback(() => {
        if (merchantId && !matchPath(pathname, ROUTES.USER.DETAILS)) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        push(ROUTES.USER.DETAILS);
    }, [merchantId, fullPath, push, pathname]);
    const handleRewardsClick = React.useCallback(() => {
        if (merchantId && !matchPath(pathname, ROUTES.USER.DETAILS)) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        push(ROUTES.USER.REWARDS);
    }, [merchantId, pathname, push, fullPath]);
    const handleActivityClick = React.useCallback(() => {
        dispatch(setReturnUrl(pathname));
        if (merchantId && !matchPath(pathname, ROUTES.USER.DETAILS)) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        push(ROUTES.USER.ACTIVITY);
    }, [dispatch, merchantId, pathname, push, fullPath]);
    const handlePaymentMethodsClick = React.useCallback(() => {
        dispatch(setReturnUrl(pathname));
        if (merchantId && !matchPath(pathname, ROUTES.USER.DETAILS)) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        push(ROUTES.USER.PAYMENT_METHODS);
    }, [dispatch, merchantId, pathname, push, fullPath]);
    const handleSignIn = React.useCallback(() => {
        dispatch(setReturnUrl(fullPath));
        if (merchantId) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        if (onAuthActionClick) {
            onAuthActionClick();
        }
        push(ROUTES.USER.LOGIN);
    }, [fullPath, dispatch, merchantId, onAuthActionClick, push]);
    const handleSignUp = React.useCallback(() => {
        if (onAuthActionClick) {
            onAuthActionClick();
        }
        dispatch(setReturnUrl(fullPath));
        if (merchantId) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
        push(ROUTES.USER.REGISTER);
    }, [onAuthActionClick, fullPath, dispatch, merchantId, push]);
    const handleLogOut = React.useCallback(async () => {
        if (onAuthActionClick) {
            await onAuthActionClick();
        }
        await dispatch(logout(userId)).unwrap();
        dispatch(resetRewards());
        if (!!matchPath(pathname, ROUTES.USER.DETAILS) && merchantId) {
            dispatch(resetReturnUrl());
        }
        if (isOnSummaryPage) {
            replace(ROUTES.MENU);
        }
        onClose();
    }, [dispatch, merchantId, onAuthActionClick, onClose, pathname, userId, isOnSummaryPage, replace]);
    const isPaymentMethodsAvailable = React.useMemo(
        () =>
            isLoggedIn &&
            !!settings?.paymentProvider &&
            paymentProvidersWithCardSupport.includes(settings?.paymentProvider) &&
            (settings?.preOrderEnabled || settings?.orderToTableEnabled || settings?.payAtTableEnabled),
        [
            isLoggedIn,
            settings?.orderToTableEnabled,
            settings?.payAtTableEnabled,
            settings?.paymentProvider,
            settings?.preOrderEnabled
        ]
    );
    return (
        <Menu anchorEl={anchorEl} id={menuId} open={!!anchorEl} onClose={onClose}>
            {!isLoggedIn && (
                <MenuItem onClick={handleSignIn}>
                    <StyledIcon>
                        <ExitToAppIcon />
                    </StyledIcon>
                    {t('ONBOARDING_SIGN_IN')}
                </MenuItem>
            )}
            {!isLoggedIn && (
                <MenuItem onClick={handleSignUp}>
                    <StyledIcon>
                        <SignUpIcon />
                    </StyledIcon>
                    {t('ONBOARDING_SIGN_UP')}
                </MenuItem>
            )}
            {isLoggedIn && (
                <MenuItem onClick={handleProfileClick}>
                    <StyledIcon>
                        <AccountCircle />
                    </StyledIcon>
                    {t('MY_DETAILS_TITLE')}
                </MenuItem>
            )}
            {isLoggedIn && (
                <MenuItem onClick={handleRewardsClick}>
                    <StyledIcon>
                        <StarOutlinedIcon />
                    </StyledIcon>
                    {t('AUTH_REWARDS')}
                </MenuItem>
            )}
            {isLoggedIn && (
                <MenuItem onClick={handleActivityClick}>
                    <StyledIcon>
                        <HistoryIcon />
                    </StyledIcon>
                    {t('MAIN_MENU_ACTIVITY')}
                </MenuItem>
            )}
            {isPaymentMethodsAvailable && (
                <MenuItem onClick={handlePaymentMethodsClick}>
                    <StyledIcon>
                        <PaymentIcon />
                    </StyledIcon>
                    {t('ACCOUNT_PAYMENT')}
                </MenuItem>
            )}
            {isLoggedIn && (
                <MenuItem onClick={handleLogOut}>
                    <StyledIcon>
                        <ExitToAppIcon />
                    </StyledIcon>
                    {t('AUTH_LOGOUT')}
                </MenuItem>
            )}
        </Menu>
    );
};
